import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPinIcon, CheckCircle2Icon, ChevronRightIcon, ChevronDownIcon, ShieldCheckIcon, ClipboardCheckIcon, UsersIcon, StarIcon, SearchIcon, ListChecksIcon, ThumbsUpIcon } from 'lucide-react';
import { services } from '../data/services';
const serviceHeroImages: Record<string, string> = {
  'walk-in-showers': 'https://cdn.magicpatterns.com/uploads/rVvwQxykYfoPiNZvEbo3zW/Modern_spa_bathroom_with_walk-in_shower.png',
  'walk-in-tubs': 'https://cdn.magicpatterns.com/uploads/uztf2XVa5Quyg5Kf3Z99xf/Contractor_installing_walk-in_tub_in_bathroom.png',
  stairlifts: 'https://images.unsplash.com/photo-1572120360610-d971b9d7767c?auto=format&fit=crop&w=1600&q=80',
  'window-replacement': 'https://cdn.magicpatterns.com/uploads/enhxpbRYFup4NWmu52YvAC/ChatGPT_Image_Apr_8,_2026,_09_00_08_PM.png',
  'gutter-guards': 'https://cdn.magicpatterns.com/uploads/nHEsPtECKST1sy11QfZeZo/Roof_worker_installing_gutter_guard.png',
  roofing: 'https://cdn.magicpatterns.com/uploads/kq5KTHt4r49NEvb6baomEs/Roofing_project_in_progress_on_suburban_home.png',
  siding: 'https://cdn.magicpatterns.com/uploads/e3vgaCMRMJVVB4ikxPQXtA/House_siding_installation_in_progress.png',
  solar: 'https://images.unsplash.com/photo-1509391366360-2e959784a276?auto=format&fit=crop&w=1600&q=80',
  'bathroom-remodel': 'https://cdn.magicpatterns.com/uploads/7LG8d1xsN1EpGcbXWXtdPd/Bathroom_remodel_in_progress__.png',
  'kitchen-remodel': 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?auto=format&fit=crop&w=1600&q=80',
  flooring: 'https://cdn.magicpatterns.com/uploads/bBvVy9ZngdjBVioxvhvvbv/Home_renovation__installing_wood_flooring.png',
  hvac: 'https://cdn.magicpatterns.com/uploads/4LVZUr8iS1pjfVRpjj65dM/HVAC_technician_servicing_outdoor_unit.png',
  painting: 'https://images.unsplash.com/photo-1562259929-b4e1fd3aef09?auto=format&fit=crop&w=1600&q=80',
  plumbing: 'https://images.unsplash.com/photo-1585704032915-c3400ca199e7?auto=format&fit=crop&w=1600&q=80',
  electrical: 'https://images.unsplash.com/photo-1621905252507-b35492cc74b4?auto=format&fit=crop&w=1600&q=80',
  fencing: 'https://images.unsplash.com/photo-1578662996442-48f60103fc96?auto=format&fit=crop&w=1600&q=80',
  insulation: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?auto=format&fit=crop&w=1600&q=80',
  doors: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?auto=format&fit=crop&w=1600&q=80',
  'garage-doors': 'https://images.unsplash.com/photo-1558036117-15d82a90b9b1?auto=format&fit=crop&w=1600&q=80',
  'home-security': 'https://cdn.magicpatterns.com/uploads/5Rf2g6s77D64RWVHLWz39v/Installing_a_home_security_camera.png',
  'pest-control': 'https://images.unsplash.com/photo-1558904541-efa843a96f01?auto=format&fit=crop&w=1600&q=80',
  landscaping: 'https://images.unsplash.com/photo-1558904541-efa843a96f01?auto=format&fit=crop&w=1600&q=80',
  cleaning: 'https://images.unsplash.com/photo-1581578731548-c64695cc6952?auto=format&fit=crop&w=1600&q=80',
  general: 'https://images.unsplash.com/photo-1504148455328-c376907d081c?auto=format&fit=crop&w=1600&q=80'
};
const serviceDescriptions: Record<string, string> = {
  'walk-in-showers': 'Walk-in showers provide a safe, accessible, and modern bathing solution for homeowners looking to upgrade their bathrooms. Designed with low or zero-threshold entries, walk-in showers significantly reduce the risk of slips and falls, making them ideal for seniors and those with mobility challenges. Investing in a walk-in shower not only enhances your daily routine but also adds lasting value and contemporary style to your home.',
  'walk-in-tubs': 'Walk-in tubs offer a therapeutic and secure bathing experience, combining the comfort of a traditional bath with essential safety features. Featuring a watertight door, low step-in threshold, and built-in seating, walk-in tubs are perfect for individuals seeking independence and relief from joint pain or arthritis. Upgrading to a walk-in tub transforms your bathroom into a personal spa while prioritizing your long-term safety and well-being.',
  stairlifts: 'Stairlifts are a reliable and practical solution for safely navigating multi-level homes without the physical strain of climbing stairs. Whether you have a straight or curved staircase, a custom-fitted stairlift restores full access to your home, allowing you to age in place comfortably. Installing a stairlift provides peace of mind for both users and their families, ensuring independence and safety on every floor.',
  'window-replacement': "Window replacement is a highly effective way to boost your home's energy efficiency, curb appeal, and overall comfort. Upgrading to modern, double or triple-pane windows helps eliminate drafts, reduces outside noise, and lowers your monthly heating and cooling costs. By choosing professional window replacement, you protect your home from the elements while enjoying a significant return on investment.",
  'gutter-guards': "Gutter guards are an essential addition to your home's exterior, designed to keep leaves, twigs, and debris out of your gutter system. By preventing clogs and overflows, gutter guards protect your roof, siding, and foundation from costly water damage. Installing high-quality gutter guards eliminates the dangerous and tedious chore of climbing ladders to clean your gutters year after year.",
  roofing: 'Professional roofing services ensure your home is protected from harsh weather conditions while enhancing its structural integrity and curb appeal. Whether you need a minor repair or a complete roof replacement, a durable new roof is your first line of defense against leaks and water damage. Investing in quality roofing materials and expert installation guarantees long-lasting peace of mind and increased property value.',
  siding: "New siding dramatically transforms your home's exterior, providing a fresh, updated look while offering superior protection against the elements. Modern siding materials, such as vinyl or fiber cement, are low-maintenance, durable, and can even improve your home's insulation and energy efficiency. Upgrading your siding is one of the most impactful ways to enhance curb appeal and secure a high return on investment.",
  solar: 'Solar panels allow homeowners to generate their own clean, renewable energy, drastically reducing or even eliminating monthly electricity bills. By harnessing the power of the sun, solar energy systems lower your carbon footprint and protect you from rising utility costs. Installing solar panels is a smart, forward-thinking investment that increases your property value while contributing to a sustainable future.',
  'bathroom-remodel': "A professional bathroom remodel breathes new life into one of the most frequently used spaces in your home, combining improved functionality with stunning design. From updating fixtures and vanities to complete layout transformations, a bathroom remodel creates a relaxing, spa-like environment tailored to your needs. Upgrading your bathroom not only enhances your daily comfort but also provides a substantial boost to your home's resale value.",
  'kitchen-remodel': "A kitchen remodel revitalizes the heart of your home, optimizing workflow, increasing storage, and updating outdated appliances and finishes. Whether you're looking for a modern culinary workspace or a warm, inviting area for entertaining, a custom kitchen remodel brings your vision to life. Investing in a high-quality kitchen renovation is widely considered one of the best ways to increase your home's overall market value.",
  flooring: "Upgrading your flooring instantly elevates the aesthetic appeal and comfort of any room in your home. With a wide variety of durable options like hardwood, luxury vinyl plank, and tile, new flooring can withstand heavy foot traffic while being easier to clean and maintain. Professional flooring installation ensures a flawless finish that enhances your home's interior design and long-term value.",
  hvac: 'A modern HVAC system is crucial for maintaining consistent, year-round indoor comfort and optimal air quality in your home. Upgrading to an energy-efficient heating and cooling system reduces your monthly utility bills while minimizing the risk of unexpected breakdowns. Professional HVAC installation and maintenance ensure your home remains a comfortable sanctuary, regardless of the weather outside.',
  painting: "Professional painting services offer a quick and cost-effective way to completely refresh and protect your home's interior or exterior. High-quality paint not only hides imperfections and stains but also defends exterior surfaces against weather damage and wear. A fresh coat of paint applied by experts instantly modernizes your space, improves indoor air quality, and boosts your home's curb appeal.",
  plumbing: 'Expert plumbing services are essential for maintaining a safe, efficient, and leak-free water system in your home. From fixing minor leaks and unclogging drains to installing new fixtures and water heaters, professional plumbers prevent costly water damage and ensure your system runs smoothly. Relying on licensed plumbing professionals guarantees peace of mind and protects the structural integrity of your property.',
  electrical: "Professional electrical services ensure your home's power system is safe, up-to-code, and capable of handling modern technological demands. Whether you need a panel upgrade, new lighting installation, or troubleshooting for flickering lights, expert electricians prevent dangerous fire hazards. Trusting licensed professionals for your electrical needs guarantees the safety of your family and the reliable operation of your appliances.",
  fencing: "Installing a new fence adds essential privacy, security, and visual appeal to your outdoor living space. Whether you choose wood, vinyl, or metal, a well-built fence clearly defines your property lines while keeping children and pets safe. Professional fencing installation enhances your home's curb appeal and provides a durable boundary that withstands the test of time.",
  insulation: "Upgrading your home's insulation is a highly effective way to improve indoor comfort and significantly lower your heating and cooling costs. Proper insulation in your attic, walls, and crawl spaces prevents energy loss, reduces drafts, and lessens the strain on your HVAC system. Investing in professional insulation services creates a more energy-efficient, environmentally friendly, and comfortable living environment.",
  doors: "Replacing your exterior doors is a simple yet impactful way to enhance your home's security, energy efficiency, and curb appeal. Modern front doors offer superior insulation, reducing drafts and lowering energy bills, while robust materials like steel or fiberglass deter intruders. A new, professionally installed door makes a welcoming first impression and provides lasting protection for your family.",
  'garage-doors': "A new garage door significantly boosts your home's exterior appearance while providing enhanced security and insulation for your garage space. Modern garage doors operate quietly, smoothly, and feature advanced safety mechanisms to protect your vehicles and belongings. Upgrading your garage door is a high-return investment that instantly modernizes your home's facade and improves daily convenience.",
  'home-security': 'A comprehensive home security system provides unparalleled peace of mind by protecting your property and loved ones around the clock. With features like 24/7 professional monitoring, smart locks, and remote smartphone access, modern security systems effectively deter burglars and unauthorized entry. Investing in professional home security ensures a rapid response in emergencies and may even lower your homeowners insurance premiums.',
  'pest-control': 'Professional pest control services protect your home from the structural damage and health risks associated with unwanted insects and rodents. Utilizing targeted, safe, and effective treatments, pest control experts eliminate current infestations and implement strategies for long-term prevention. Regular pest control maintenance ensures your living environment remains clean, safe, and comfortable for your family and pets.',
  landscaping: "Professional landscaping transforms your yard into a beautiful, functional outdoor oasis that extends your living space. From lush garden designs and tree planting to hardscaping elements like patios and walkways, expert landscaping improves drainage and prevents soil erosion. Investing in quality landscaping significantly enhances your home's curb appeal and can substantially increase its overall market value.",
  cleaning: 'Professional deep cleaning services save you valuable time while ensuring your home remains spotless, hygienic, and welcoming. Expert cleaners use high-grade products and techniques to eliminate dust, allergens, and grime from hard-to-reach areas, improving your indoor air quality. Relying on a professional cleaning service reduces stress and provides consistently exceptional results that keep your home looking its best.',
  general: 'General repair and handyman services offer a convenient, cost-effective solution for tackling your home maintenance to-do list. From minor plumbing and electrical fixes to drywall repair and furniture assembly, skilled professionals ensure tasks are completed safely and correctly. Hiring a reliable handyman prevents small issues from escalating into major problems, keeping your home in excellent condition year-round.'
};
interface NearMeServiceProps {
  serviceSlug: string;
  onNavigate: (path: string) => void;
}
export function NearMeService({
  serviceSlug,
  onNavigate
}: NearMeServiceProps) {
  const [zipCode, setZipCode] = useState('');
  const [isValidZip, setIsValidZip] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const service = services.find(s => s.slug === serviceSlug);
  if (!service) {
    return <div className="py-20 text-center" data-id="element-2026">Service not found</div>;
  }
  const heroImage = serviceHeroImages[serviceSlug] || 'https://images.unsplash.com/photo-1504148455328-c376907d081c?auto=format&fit=crop&w=1600&q=80';
  const description = serviceDescriptions[serviceSlug] || service.description;
  const handleZipChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/\D/g, '').slice(0, 5);
    setZipCode(val);
    setIsValidZip(val.length === 5);
  };
  const handleContinue = () => {
    if (isValidZip) {
      onNavigate(`/${serviceSlug}/quote-form?zip=${zipCode}`);
    }
  };
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(value);
  };
  return <div className="min-h-screen bg-white font-sans" data-id="element-2027">
      {/* Hero Section */}
      <section className="pt-24 pb-32 relative overflow-hidden" data-id="element-2028">
        {/* Background Image */}
        <div className="absolute inset-0 z-0" data-id="element-2029">
          <img src={heroImage} alt={`${service.name} background`} className="w-full h-full object-cover" data-id="element-2030" />
          <div className="absolute inset-0 bg-blue-900/60" data-id="element-2031" />
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center" data-id="element-2032">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-4xl md:text-6xl font-extrabold text-white mb-6 leading-tight" data-id="element-2033">
            Top {service.name} Contractors Near Me
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="text-xl text-blue-100 mb-10 max-w-2xl mx-auto" data-id="element-2034">
            Enter your ZIP Code to get matched with the top professionals near
            you.
          </motion.p>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className="max-w-xl mx-auto bg-white p-2 rounded-2xl shadow-2xl flex flex-col sm:flex-row gap-2" data-id="element-2035">
            <div className="relative flex-1" data-id="element-2036">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-2037">
                <MapPinIcon className="h-5 w-5 text-gray-400" data-id="element-2038" />
              </div>
              <input type="text" value={zipCode} onChange={handleZipChange} placeholder="Enter ZIP Code" className="w-full h-14 pl-12 pr-4 text-lg font-medium bg-transparent border-none focus:ring-0 text-gray-900" maxLength={5} data-id="element-2039" />
            </div>
            <button onClick={handleContinue} disabled={!isValidZip} className={`h-14 px-8 rounded-xl font-bold text-lg text-white transition-all sm:w-auto w-full ${isValidZip ? 'bg-amber-500 hover:bg-amber-600 shadow-lg shadow-amber-500/30' : 'bg-gray-300 cursor-not-allowed'}`} data-id="element-2040">
              Start matching
            </button>
          </motion.div>
          <p className="text-sm text-blue-200 mt-4" data-id="element-2041">
            Free, no-obligation estimates.
          </p>
        </div>
      </section>

      {/* Breadcrumb */}
      <div className="bg-white py-4 border-b border-gray-100" data-id="element-2042">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-center text-sm text-gray-500" data-id="element-2043">
          <button onClick={() => onNavigate('/')} className="hover:text-[#2563EB]" data-id="element-2044">
            Home
          </button>
          <ChevronRightIcon className="w-4 h-4 mx-2" data-id="element-2045" />
          <span className="hover:text-[#2563EB] cursor-pointer" data-id="element-2046">Near Me</span>
          <ChevronRightIcon className="w-4 h-4 mx-2" data-id="element-2047" />
          <span className="text-gray-900 font-medium" data-id="element-2048">{service.name}</span>
        </div>
      </div>

      {/* How It Works Section (SEO) */}
      <section className="py-20 bg-white" data-id="element-2049">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2050">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-16" data-id="element-2051">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-2052">
              How It Works: Finding {service.name} Pros
            </h2>
            <p className="text-lg text-slate-600" data-id="element-2053">
              Our simple 3-step process makes it easy to find and hire the best
              local professionals.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 relative" data-id="element-2054">
            <div className="hidden md:block absolute top-12 left-[16%] right-[16%] h-0.5 bg-slate-200 z-0" data-id="element-2055"></div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="relative z-10 flex flex-col items-center text-center" data-id="element-2056">
              <div className="w-24 h-24 bg-blue-50 rounded-full flex items-center justify-center mb-6 border-4 border-white shadow-sm" data-id="element-2057">
                <SearchIcon className="w-10 h-10 text-[#2563EB]" data-id="element-2058" />
              </div>
              <div className="w-8 h-8 bg-[#2563EB] text-white rounded-full flex items-center justify-center font-bold text-sm absolute top-0 right-1/2 translate-x-12 -translate-y-2 border-2 border-white" data-id="element-2059">
                1
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3" data-id="element-2060">
                Enter Your ZIP Code
              </h3>
              <p className="text-slate-600" data-id="element-2061">
                Tell us where you need {service.name.toLowerCase()} services to
                start the matching process.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="relative z-10 flex flex-col items-center text-center" data-id="element-2062">
              <div className="w-24 h-24 bg-blue-50 rounded-full flex items-center justify-center mb-6 border-4 border-white shadow-sm" data-id="element-2063">
                <UsersIcon className="w-10 h-10 text-[#2563EB]" data-id="element-2064" />
              </div>
              <div className="w-8 h-8 bg-[#2563EB] text-white rounded-full flex items-center justify-center font-bold text-sm absolute top-0 right-1/2 translate-x-12 -translate-y-2 border-2 border-white" data-id="element-2065">
                2
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3" data-id="element-2066">
                Get Matched
              </h3>
              <p className="text-slate-600" data-id="element-2067">
                We'll connect you with top-rated, vetted{' '}
                {service.name.toLowerCase()} professionals in your local area.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.3
          }} className="relative z-10 flex flex-col items-center text-center" data-id="element-2068">
              <div className="w-24 h-24 bg-blue-50 rounded-full flex items-center justify-center mb-6 border-4 border-white shadow-sm" data-id="element-2069">
                <ListChecksIcon className="w-10 h-10 text-[#2563EB]" data-id="element-2070" />
              </div>
              <div className="w-8 h-8 bg-[#2563EB] text-white rounded-full flex items-center justify-center font-bold text-sm absolute top-0 right-1/2 translate-x-12 -translate-y-2 border-2 border-white" data-id="element-2071">
                3
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3" data-id="element-2072">
                Compare & Hire
              </h3>
              <p className="text-slate-600" data-id="element-2073">
                Review free, no-obligation quotes, compare options, and hire the
                right pro for your project.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* About Section (SEO) */}
      <section className="py-20 bg-slate-50" data-id="element-2074">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2075">
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20" data-id="element-2076">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="flex-1 w-full" data-id="element-2077">
              <img src={heroImage} alt={`${service.name} professional service`} className="rounded-2xl shadow-lg w-full object-cover h-[360px]" data-id="element-2078" />
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="flex-1" data-id="element-2079">
              <span className="inline-block text-sm font-bold text-[#2563EB] uppercase tracking-wider mb-3" data-id="element-2080">
                Why Homeowners Choose Us
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-5" data-id="element-2081">
                {service.name} You Can Trust
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-8" data-id="element-2082">
                {description}
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-2083">
                <div className="flex items-start gap-3" data-id="element-2084">
                  <div className="mt-0.5 bg-green-100 rounded-full p-1 flex-shrink-0" data-id="element-2085">
                    <CheckCircle2Icon className="w-5 h-5 text-green-600" data-id="element-2086" />
                  </div>
                  <span className="text-slate-700 font-medium" data-id="element-2087">
                    Free, no-obligation quotes
                  </span>
                </div>
                <div className="flex items-start gap-3" data-id="element-2088">
                  <div className="mt-0.5 bg-green-100 rounded-full p-1 flex-shrink-0" data-id="element-2089">
                    <CheckCircle2Icon className="w-5 h-5 text-green-600" data-id="element-2090" />
                  </div>
                  <span className="text-slate-700 font-medium" data-id="element-2091">
                    Vetted local professionals
                  </span>
                </div>
                <div className="flex items-start gap-3" data-id="element-2092">
                  <div className="mt-0.5 bg-green-100 rounded-full p-1 flex-shrink-0" data-id="element-2093">
                    <CheckCircle2Icon className="w-5 h-5 text-green-600" data-id="element-2094" />
                  </div>
                  <span className="text-slate-700 font-medium" data-id="element-2095">
                    Compare multiple options
                  </span>
                </div>
                <div className="flex items-start gap-3" data-id="element-2096">
                  <div className="mt-0.5 bg-green-100 rounded-full p-1 flex-shrink-0" data-id="element-2097">
                    <CheckCircle2Icon className="w-5 h-5 text-green-600" data-id="element-2098" />
                  </div>
                  <span className="text-slate-700 font-medium" data-id="element-2099">
                    Fast, easy matching
                  </span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Cost Section (SEO) */}
      <section className="py-20 bg-white" data-id="element-2100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2101">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-12" data-id="element-2102">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-2103">
              How Much Does {service.name} Cost?
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-2104">
              Understanding the typical costs for {service.name.toLowerCase()}{' '}
              can help you budget effectively for your home improvement project.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10" data-id="element-2105">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="bg-slate-50 rounded-2xl p-8 text-center shadow-sm border border-slate-200" data-id="element-2106">
              <h3 className="text-lg font-semibold text-slate-500 mb-2" data-id="element-2107">
                Low End
              </h3>
              <div className="text-4xl font-extrabold text-slate-900" data-id="element-2108">
                {formatCurrency(service.costRange.low)}
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="bg-slate-50 rounded-2xl p-8 text-center shadow-md border-2 border-[#2563EB] relative transform md:-translate-y-4" data-id="element-2109">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-[#2563EB] text-white px-4 py-1 rounded-full text-sm font-bold" data-id="element-2110">
                National Average
              </div>
              <h3 className="text-lg font-semibold text-slate-500 mb-2 mt-2" data-id="element-2111">
                Average Cost
              </h3>
              <div className="text-5xl font-extrabold text-[#2563EB]" data-id="element-2112">
                {formatCurrency(service.costRange.average)}
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.3
          }} className="bg-slate-50 rounded-2xl p-8 text-center shadow-sm border border-slate-200" data-id="element-2113">
              <h3 className="text-lg font-semibold text-slate-500 mb-2" data-id="element-2114">
                High End
              </h3>
              <div className="text-4xl font-extrabold text-slate-900" data-id="element-2115">
                {formatCurrency(service.costRange.high)}
              </div>
            </motion.div>
          </div>

          <p className="text-center text-slate-500 text-sm max-w-3xl mx-auto" data-id="element-2116">
            *Please note that actual costs for {service.name.toLowerCase()} will
            vary based on your specific location, the scope of the project,
            materials chosen, and current labor rates in your area. Requesting
            multiple quotes is the best way to get an accurate estimate.
          </p>
        </div>
      </section>

      {/* Trust Signals Section (SEO) */}
      <section className="py-20 bg-slate-50" data-id="element-2117">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2118">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-16" data-id="element-2119">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-2120">
              Why Choose {service.name} Professionals Near You?
            </h2>
            <p className="text-lg text-slate-600" data-id="element-2121">
              We partner with the best in the business to ensure your project is
              a success.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-2122">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 flex flex-col items-center text-center" data-id="element-2123">
              <ShieldCheckIcon className="w-12 h-12 text-green-500 mb-4" data-id="element-2124" />
              <h3 className="text-lg font-bold text-slate-900 mb-2" data-id="element-2125">
                Licensed & Insured
              </h3>
              <p className="text-sm text-slate-600" data-id="element-2126">
                Connect with qualified professionals who meet industry
                standards.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 flex flex-col items-center text-center" data-id="element-2127">
              <ClipboardCheckIcon className="w-12 h-12 text-amber-500 mb-4" data-id="element-2128" />
              <h3 className="text-lg font-bold text-slate-900 mb-2" data-id="element-2129">
                Free Quotes
              </h3>
              <p className="text-sm text-slate-600" data-id="element-2130">
                Receive multiple no-obligation estimates to compare pricing.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.3
          }} className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 flex flex-col items-center text-center" data-id="element-2131">
              <MapPinIcon className="w-12 h-12 text-[#2563EB] mb-4" data-id="element-2132" />
              <h3 className="text-lg font-bold text-slate-900 mb-2" data-id="element-2133">
                Local Expertise
              </h3>
              <p className="text-sm text-slate-600" data-id="element-2134">
                Work with contractors who understand your area's specific
                requirements.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.4
          }} className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 flex flex-col items-center text-center" data-id="element-2135">
              <ThumbsUpIcon className="w-12 h-12 text-purple-500 mb-4" data-id="element-2136" />
              <h3 className="text-lg font-bold text-slate-900 mb-2" data-id="element-2137">
                Quality Service
              </h3>
              <p className="text-sm text-slate-600" data-id="element-2138">
                Find vetted pros committed to delivering excellent results.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white" data-id="element-2139">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2140">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-slate-900 mb-16" data-id="element-2141">
            {service.name} Made Simple, Quick, and Cost-Effective!
          </h2>

          <div className="flex flex-col md:flex-row items-center gap-12 lg:gap-24" data-id="element-2142">
            <div className="flex-1 space-y-6" data-id="element-2143">
              {service.benefits.map((benefit, idx) => <motion.div key={idx} initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: idx * 0.1
            }} className="flex items-start gap-4" data-id="element-2144">
                  <div className="mt-1 bg-green-100 rounded-full p-1 flex-shrink-0" data-id="element-2145">
                    <CheckCircle2Icon className="w-6 h-6 text-green-600" data-id="element-2146" />
                  </div>
                  <p className="text-lg font-bold text-slate-800" data-id="element-2147">{benefit}</p>
                </motion.div>)}
            </div>
            <div className="flex-1 w-full" data-id="element-2148">
              <img src={heroImage} alt={`${service.name} example`} className="rounded-3xl shadow-xl w-full object-cover h-[400px]" data-id="element-2149" />
            </div>
          </div>
        </div>
      </section>

      {/* FAQs Section */}
      <section className="py-20 bg-slate-50" data-id="element-2150">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2151">
          <h2 className="text-3xl font-bold text-center text-slate-900 mb-12" data-id="element-2152">
            Frequently Asked Questions
          </h2>
          <div className="space-y-4" data-id="element-2153">
            {service.faqs.map((faq, idx) => <div key={idx} className="border border-slate-200 rounded-2xl overflow-hidden bg-white" data-id="element-2154">
                <button onClick={() => setOpenFaq(openFaq === idx ? null : idx)} className="w-full px-6 py-5 text-left flex items-center justify-between focus:outline-none" data-id="element-2155">
                  <span className="font-bold text-slate-900 text-lg pr-8" data-id="element-2156">
                    {faq.question}
                  </span>
                  <ChevronDownIcon className={`w-5 h-5 text-slate-400 transition-transform duration-200 flex-shrink-0 ${openFaq === idx ? 'rotate-180' : ''}`} data-id="element-2157" />
                </button>
                {openFaq === idx && <div className="px-6 pb-5 text-slate-600 leading-relaxed" data-id="element-2158">
                    {faq.answer}
                  </div>}
              </div>)}
          </div>
        </div>
      </section>
    </div>;
}